import { StateProvider, StateParams, StateService } from "@uirouter/angularjs";

import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../records/server/serverUtility";
import { vsName as ProjectCostDetailComponentName } from "./views/info/component";
import { vsObjectName as projectName } from "./shared/configs/project";
import { vsObjectName as projectCostName } from "./shared/configs/projectCost";
import {
  getResolveObjectFromGridConfiguration,
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../records/views/recordGridView";
import { ConfigurableGridFormViewConfig } from "../records/views/configurableGridFormView/controller";
import {
  vsObjectName as projectMachineName,
  vsMachineColumnSetName as projectMachineMachineColumnsSetName,
} from "./shared/configs/projectMachines";

import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../records/grids/columns/columnDefinitionService";
import { vsComponentName as configurableGridFormViewComponentName } from "../records/views/configurableGridFormView";
import { GridSelectionChangedArgs } from "../records/grids/controller";

import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../records/grids/settings/gridSettingsService";

import { machineObjectName } from "./../machines/shared/names";
import {
  FormViewService,
  vsComponentName as FormViewComponentName,
  vsServiceName as FormViewServiceName,
} from "../records/forms/formView";
import { Record } from "../records/server/crud";
import { ServiceResponse } from "../records/server/query";
import {
  ConfigurableGridFormViewService,
  vsServiceName as ConfigurableGridFormViewServiceName,
} from "../records/views/configurableGridFormView/service";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../records/grids/columns/columnDefinitionSetService";

export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state("main.project", {
      url: "project",
      component: RecordGridViewComponentName,
      resolve: getResolveObjectFromGridConfiguration([
        RecordGridViewServiceName,
        ColumnDefinitionServiceName,
        "$state",
        (
          recordGridViewService: RecordGridViewService,
          columnDefinitionService: ColumnDefinitionService,
          $state: StateService
        ) => {
          const config =
            recordGridViewService.getGridConfiguration(projectName)!;

          config.columns = columnDefinitionService.addInfoEditColumns(
            config.columns,
            {
              infoSref: ".info.machines",
              editSref: ".info.edit",
              editPermission: "projectCostEdit",
            }
          );
          config.onAddNew = () => $state.go(".add", {}).then(() => {});

          return config;
        },
      ]),
    })
    .state("main.projectcost", {
      url: "project/cost",
      component: RecordGridViewComponentName,
      resolve: getResolveObjectFromGridConfiguration([
        RecordGridViewServiceName,
        ColumnDefinitionServiceName,
        "$state",
        (
          recordGridViewService: RecordGridViewService,
          columnDefinitionService: ColumnDefinitionService,
          $state: StateService
        ) => {
          const config =
            recordGridViewService.getGridConfiguration(projectCostName)!;

          return config;
        },
      ]),
    })
    .state("main.project.add", {
      url: "/add",
      params: {
        cloneId: undefined,
      },
      views: {
        "@^.^": {
          component: FormViewComponentName,
        },
      },
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          (formViewService: FormViewService, $state: StateService) => {
            const config = formViewService.getFormViewConfig(projectName)!;
            config.submitForm = (args) =>
              formViewService
                .saveForm({
                  formObject: args.model,
                  formViewController: args.formViewController,
                  url: `/api/project/Project`,
                })
                .then((r) =>
                  $state.go("^.info.machines", { id: (<any>r).Result.Id })
                );
            return config;
          },
        ],
        [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) => {},
        ]
      ),
    })
    .state("main.project.info", {
      url: "/{id:int}/info",
      views: {
        "@^.^": {
          component: ProjectCostDetailComponentName,
        },
      },
      resolve: {
        project: [
          ServerUtilityName,
          "$stateParams",
          (serverUtility: ServerUtility, $stateParams: StateParams) =>
            serverUtility
              .getById<ServiceResponse<Record>>({
                url: "/api/project/Project",
                id: $stateParams["id"],
              })
              .then((apiResponse) => apiResponse?.Result),
        ],
      },
    })

    .state("main.project.info.edit", {
      url: "/edit",
      component: FormViewComponentName,
      resolve: {
        ...FormViewService.getResolveObjectFromFormViewConfig(
          [
            FormViewServiceName,
            "$state",
            (formViewService: FormViewService, $state: StateService) => {
              const config = formViewService.getFormViewConfig(projectName)!;

              config.submitForm = (args: any) =>
                formViewService
                  .saveForm({
                    formObject: args.model,
                    formViewController: args.formViewController,
                    url: `/api/project/Project`,
                  })
                  .then(() =>
                    $state.go("^.^.info.machines", { id: args.model.Id })
                  );

              return config;
            },
          ],
          [
            "$stateParams",
            ServerUtilityName,
            async ($stateParams: StateParams, serverUtility: ServerUtility) => {
              const response = (await serverUtility.getById({
                url: `/api/project/Project`,
                id: $stateParams["id"],
              })) as { Result: any[] };

              return response?.Result || null;
            },
          ]
        ),
        onCancel: [
          "$state",
          ($state: StateService) => [() => $state.go("^.machines")],
        ],
      },
    })

    .state("main.project.info.machines", {
      url: "/machines",
      component: configurableGridFormViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          ColumnDefinitionServiceName,
          ColumnDefinitionSetServiceName,
          GridSettingsServiceName,
          (
            recordGridViewService: RecordGridViewService,
            columnDefinitionService: ColumnDefinitionService,
            columnDefinitionSetService: ColumnDefinitionSetService,
            gridSettingsService: GridSettingsService
          ) => {
            const config =
              recordGridViewService.getGridConfiguration(projectMachineName)!;

            config.gridSettings = gridSettingsService.getGridSettings(
              Symbol(projectMachineMachineColumnsSetName)
            );
            config.columns =
              columnDefinitionSetService.getColumnDefinitionSet(
                projectMachineName
              );

            config.columns = [
              columnDefinitionService.getLinkColDef({
                colId: "detail",
                headerName: " ",
                iconClass: "fa fa-info-circle text-primary",
                stateExpression:
                  "main.machines.detail.redirect({machineId: data.MachineId})",
              }),
              ...(config.columns ?? []),
            ];

            return config;
          },
        ],
        config: [
          "gridConfiguration",
          "project",
          (
            gridConfiguration: GridConfiguration,
            project: Record
          ): ConfigurableGridFormViewConfig => {
            return {
              defaultQuery: {
                ProjectId: project.Id,
              },
              additionalToggleButtons: [],
              gridConfiguration,
              navButtonText: "Edit",
              navButtonIcon: "fa-pencil",
              navPath: ".edit",
              navButtonPermission: "machineEdit",
              showNavButton: true,
            };
          },
        ],
      },
    })
    .state("main.project.info.machines.edit", {
      url: "/edit",
      views: {
        "@^.^": {
          component: configurableGridFormViewComponentName,
        },
      },
      resolve: {
        selectedIds: [
          ServerUtilityName,
          "project",
          (serverUtility: ServerUtility, project: Record) =>
            serverUtility
              .fetchAll({
                url: `/api/project/${project.Id}/machines`,
                query: {},
              })
              .then((items) => items.map((i: any) => i.MachineId)),
        ],

        gridConfiguration: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService
          ) => {
            const config =
              recordGridViewService.getGridConfiguration(machineObjectName)!;

            config.gridSettings = gridSettingsService.getGridSettings(
              Symbol("projectMachines.edit")
            );

            return config;
          },
        ],

        config: [
          ServerUtilityName,
          "project",
          "gridConfiguration",
          "selectedIds",
          ConfigurableGridFormViewServiceName,
          (
            serverUtility: ServerUtility,
            project: Record,
            gridConfiguration: GridConfiguration,
            selectedIds: number[],
            configurableGridFormViewService: ConfigurableGridFormViewService
          ): ConfigurableGridFormViewConfig => {
            return {
              navButtonIcon: "fa-pencil",
              navButtonText: "Done",
              navPath: "^",
              showNavButton: true,
              showSelectButtons: true,
              defaultQuery: {
                Ids: selectedIds.length === 0 ? [0] : selectedIds,
              },
              additionalToggleButtons: [
                configurableGridFormViewService.idDefaultQueryToggleButton(
                  true
                ),
              ],
              gridSelectionChanged: (args: GridSelectionChangedArgs) => {
                const machineId = args.data.MachineId as number;
                const url = `/api/project/${project.Id}/machines`;

                if (args.selected) {
                  serverUtility.post({
                    url,
                    postObject: {
                      MachineId: machineId,
                    },
                  });
                } else {
                  serverUtility.sendApiRequest({
                    url: url + `/byMachineId`,
                    method: "delete",
                    data: {
                      MachineId: machineId,
                    },
                  });
                }
              },
              gridConfiguration,
              selectedIds,
            };
          },
        ],
      },
    });
}
routeConfig.$inject = ["$stateProvider"];
